/**
 * 新订单·手机端：仅选未分配产康安排 + 分配产康师与排期（无顶栏导航、无门店/订单类型切换）
 * antd-mobile 全局样式在 src/index.js 统一引入，与 PC 端 antd 的兼容见 @/theme/antdMobileCompat.less
 * 排期表单与桌面派单「新订单」一致：MaternityCenterDispatcherContent（embedMode=newOrdersMobile）
 * → UnassignedProjectPlanBookPanel，内嵌 SingleProjectAppointmentForm（预约日为 antd-mobile CalendarPicker ↔ CalendarPickerView，见 MobileBottomSheetDateField）。
 *
 * 门店 id：
 * - 小程序内嵌 H5：务必在 webview 地址上带 `?storeId=门店ID`。带参时本页**不再请求门店列表**，首屏直接拉订单，明显缩短白屏（嵌入模式不展示门店下拉，无需 jStoreOptions）。
 * - 若同会话内 Redux 已有门店 options，无 URL 参数时直接用缓存首家门店拉订单，跳过等待门店列表接口（冷启动仍须走该接口）。
 * - PC 派单「新订单」走 Dispatcher 路由，在 MaternityCenterDispatcherContent 里用筛选项选择门店，不经过本页 URL。
 *
 * 路由地址（勿省略 /client）：`/client/order/dispatchNewOrdersMobile`
 * 小程序 web-view：须在公众平台配置业务域名（HTTPS）；否则页面无法加载。
 */
import React, { useMemo } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Alert } from 'antd'
import {
  fetchOrdersByType,
  ORDER_LIST_REDUX_SCOPE,
  resetDispatchOrderListState
} from '@/redux/order/maternityCenterOrderSlice'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { clearSelectedOrder } from '@/redux/order/dispatchPageSlice'
import { getParsedUserAgent } from '@/utils/userAgentEnv'
import { initVConsole } from '@/utils/initVConsole'
import ClientRouteBootSkeleton from '@/components/ClientRouteBootSkeleton'
import MaternityCenterDispatcherContent from '../components/MaternityCenterDispatcherContent'
import '../DispatcherPageList.less'
import './NewOrdersDispatchMobilePage.less'

const getNewOrdersMobileQuery = (storeId) => ({
  storeId: storeId || '',
  checkInStatus: 1,
  keyword: '',
  pageNumber: 1,
  pageSize: 20
})

const normalizeStoreId = (value) => {
  return value != null && String(value).trim() !== '' ? String(value).trim() : ''
}

const getFirstStoreIdFromStoreApiData = (raw) => {
  let list = raw
  if (list && !Array.isArray(list) && Array.isArray(list.records)) {
    list = list.records
  }
  if (!Array.isArray(list) || list.length === 0) return ''
  const item = list[0]
  const rawId = item?.id ?? item?.value
  return normalizeStoreId(rawId)
}

/** 门店列表请求超时（毫秒），避免弱网一直转圈 */
const FETCH_JSTORE_LIST_TIMEOUT_MS = 25000

/** useSelector 缺省兜底须用稳定引用，否则每次 render 都是新 `{}`，子树无意义重绘（手机端易卡顿） */
const EMPTY_ORDER_OBJ = {}
const EMPTY_DISPATCH_PAGE = {}
const EMPTY_JSTORE_OPTIONS = []

/** 从路由查询串取门店（小程序 webview 推荐 `?storeId=xxx`） */
function getStoreIdFromSearch (search) {
  if (search == null || search === '') return ''
  const q = search.startsWith('?') ? search : `?${search}`
  const sp = new URLSearchParams(q)
  const v = sp.get('storeId') ?? sp.get('storeid') ?? sp.get('store_id')
  return normalizeStoreId(v)
}

class NewOrdersDispatchMobilePage extends React.Component {
  constructor(props) {
    super(props)
    this.state = { storeReady: false, noStore: false }
    this._isUnmounted = false
    this._bootstrapSeq = 0
  }

  componentDidMount () {
    // initVConsole({ force: true })
    this._isUnmounted = false
    this.bootstrapOrders()
  }

  componentDidUpdate (prevProps) {
    const prevUrlStoreId = normalizeStoreId(prevProps.urlStoreId)
    const nextUrlStoreId = normalizeStoreId(this.props.urlStoreId)
    if (prevUrlStoreId !== nextUrlStoreId) {
      this.bootstrapOrders()
    }
  }

  componentWillUnmount () {
    this._isUnmounted = true
    this.props.dispatch(resetDispatchOrderListState())
  }

  bootstrapOrders = () => {
    const seq = ++this._bootstrapSeq
    const { dispatch, urlStoreId, jStoreOptions } = this.props
    const fromUrl = normalizeStoreId(urlStoreId)
    dispatch(clearSelectedOrder())

    const loadStores = () => dispatch(fetchJStoreList({ query: {}, dataType: 'optionErp' })).unwrap()

    /** 嵌入手机页不展示门店 Select，有明确 storeId 时无需拉门店列表，省一整次 HTTP 往返 */
    const bootOrdersOnly = (storeId) => {
      if (this._isUnmounted) return
      this.setState({ storeReady: true, noStore: false })
      void this.fetchOrders(getNewOrdersMobileQuery(storeId)).catch(() => {})
    }

    if (fromUrl) {
      bootOrdersOnly(fromUrl)
      return
    }

    const cachedFirstStoreId = getFirstStoreIdFromStoreApiData(jStoreOptions)
    if (cachedFirstStoreId) {
      bootOrdersOnly(cachedFirstStoreId)
      return
    }

    ;(async () => {
      try {
        if (!this._isUnmounted) {
          this.setState({ storeReady: false, noStore: false })
        }
        const payload = await Promise.race([
          loadStores(),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('STORE_LIST_TIMEOUT')), FETCH_JSTORE_LIST_TIMEOUT_MS)
          })
        ])
        if (this._isUnmounted || seq !== this._bootstrapSeq) return
        const raw = payload?.data
        const list = Array.isArray(raw) ? raw.filter((item) => item.extra === 'erp') : raw
        const firstStoreId = getFirstStoreIdFromStoreApiData(list)
        if (!firstStoreId) {
          this.setState({ storeReady: true, noStore: true })
          return
        }
        this.setState({ storeReady: true, noStore: false })
        await this.fetchOrders(getNewOrdersMobileQuery(firstStoreId)).catch(() => {})
      } catch {
        if (this._isUnmounted || seq !== this._bootstrapSeq) return
        this.setState({ storeReady: true, noStore: true })
      }
    })()
  }

  fetchOrders = (uiQuery, options = {}) => {
    const { dispatch } = this.props
    const { silent = false } = options
    return dispatch(
      fetchOrdersByType({
        query: uiQuery,
        orderType: 'maternity_center',
        maternityCenterListMode: 'dispatchUnassigned',
        orderListScope: ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE,
        silent
      })
    )
  }

  refreshList = () => {
    const { urlStoreId, orderObj } = this.props
    const fromUrl = urlStoreId != null && String(urlStoreId).trim() !== ''
      ? String(urlStoreId).trim()
      : ''
    const prev = orderObj?.dispatchOrderList?.query
    const q = prev
      ? { ...prev, storeId: fromUrl || prev.storeId || '' }
      : getNewOrdersMobileQuery(fromUrl || '')
    this.fetchOrders(q)
  }

  handleFilterChange = (mergedQuery) => {
    const { urlStoreId } = this.props
    const pin =
      urlStoreId != null && String(urlStoreId).trim() !== ''
        ? String(urlStoreId).trim()
        : null
    const next = pin != null ? { ...mergedQuery, storeId: pin } : mergedQuery
    const prevQ = this.props.orderObj?.dispatchOrderList?.query || {}
    if (
      String(prevQ.storeId ?? '') !== String(next.storeId ?? '') ||
      String(prevQ.checkInStatus ?? '') !== String(next.checkInStatus ?? '')
    ) {
      this.props.dispatch(clearSelectedOrder())
    }
    this.fetchOrders(next)
  }

  render() {
    const { orderObj, dispatchPage, jStoreOptions, dispatch } = this.props
    const { storeReady, noStore } = this.state
    if (!storeReady) {
      return (
        <div className="dispatcher-page-list new-orders-dispatch-mobile new-orders-dispatch-mobile--loading">
          <ClientRouteBootSkeleton />
        </div>
      )
    }
    if (noStore) {
      return (
        <div className="dispatcher-page-list new-orders-dispatch-mobile">
          <Alert type="warning" showIcon message="未找到可用门店" description="请先在系统配置门店后再使用本页。" />
        </div>
      )
    }
    return (
      <div className="dispatcher-page-list new-orders-dispatch-mobile">
        <MaternityCenterDispatcherContent
          orderType="maternity_center"
          orderObj={orderObj}
          dispatchPage={dispatchPage}
          jStoreOptions={jStoreOptions}
          dispatch={dispatch}
          fetchOrders={this.fetchOrders}
          refreshList={this.refreshList}
          handleFilterChange={this.handleFilterChange}
          embedMode="newOrdersMobile"
          embedUrlStoreId={this.props.urlStoreId}
          embedMobileUaDevice={this.props.embedMobileUaDevice}
        />
      </div>
    )
  }
}

const NewOrdersDispatchMobilePageWrapper = () => {
  const reduxDispatch = useDispatch()
  const { search } = useLocation()
  const urlStoreId = getStoreIdFromSearch(search)
  const orderObj = useSelector(
    (state) => state.order?.order ?? EMPTY_ORDER_OBJ,
    shallowEqual
  )
  const dispatchPage = useSelector(
    (state) => state.order?.dispatchPage ?? EMPTY_DISPATCH_PAGE,
    shallowEqual
  )
  const jStoreOptions = useSelector(
    (state) => state.components?.getJStoreList?.data ?? EMPTY_JSTORE_OPTIONS,
    shallowEqual
  )
  const embedMobileUaDevice = useMemo(() => {
    try {
      return !!getParsedUserAgent().isMobileDevice
    } catch {
      return true
    }
  }, [])
  return (
    <NewOrdersDispatchMobilePage
      dispatch={reduxDispatch}
      orderObj={orderObj}
      dispatchPage={dispatchPage}
      jStoreOptions={jStoreOptions}
      urlStoreId={urlStoreId}
      embedMobileUaDevice={embedMobileUaDevice}
    />
  )
}

export default NewOrdersDispatchMobilePageWrapper
